import { Linkedin, Twitter, Mail, Phone, MapPin } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/site";
import { SERVICES } from "@/data/services";
import { useSectionNav } from "@/lib/useSectionNav";
import { Link } from "react-router-dom";
import wordmarkWhite from "@/assets/echo-bravo-wordmark-white.png";

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "#" },
  { icon: Twitter, label: "X", href: "#" },
];

const Footer = () => {
  const year = new Date().getFullYear();
  const goNav = useSectionNav();
  return (
    <footer data-testid="footer" className="bg-navy-900 text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <img src={wordmarkWhite} alt="Echo Bravo" className="h-8 w-auto" />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-silver-300/70">
              Independent maritime consultancy delivering fleet, operational,
              technical and strategic advisory to the global shipping industry.
            </p>
            <div className="mt-7 flex gap-3">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                    aria-label={s.label}
                    className="inline-flex h-10 w-10 items-center justify-center border border-white/15 text-white/70 transition-all duration-300 hover:border-brand-blue hover:bg-brand-blue hover:text-white"
                  >
                    <Icon size={17} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Navigate */}
          <div className="lg:col-span-2">
            <div className="eb-overline text-silver-400">Navigate</div>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <button
                    data-testid={`footer-nav-${l.href.replace("#", "")}`}
                    onClick={() => goNav(l.href)}
                    className="text-sm text-silver-300/80 hover:text-white transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <div className="eb-overline text-silver-400">Services</div>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-left text-sm text-silver-300/80 hover:text-white transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="eb-overline text-silver-400">Contact</div>
            <ul className="mt-5 space-y-4 text-sm text-silver-300/80">
              <li className="flex gap-3">
                <Mail size={16} className="mt-0.5 shrink-0 text-brand-blue" />
                <a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className="hover:text-white transition-colors whitespace-nowrap">{COMPANY.email}</a>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="mt-0.5 shrink-0 text-brand-blue" />
                <a href={`tel:${COMPANY.phoneHref}`} className="hover:text-white transition-colors">{COMPANY.phone}</a>
              </li>
              <li className="flex gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0 text-brand-blue" />
                <span>{COMPANY.address.map((line, i) => <span key={i} className="block">{line}</span>)}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Oversized faint wordmark — SVG text scales to fit, never overflows */}
        <div className="mt-16 border-t border-white/10 pt-10">
          <svg
            viewBox="0 0 1000 150"
            className="w-full h-auto select-none"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <text
              x="0"
              y="122"
              textLength="1000"
              lengthAdjust="spacingAndGlyphs"
              fontFamily="'Cabinet Grotesk', Manrope, sans-serif"
              fontWeight="900"
              fontSize="150"
              fill="rgba(255,255,255,0.05)"
            >
              ECHO BRAVO
            </text>
          </svg>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-silver-400">
          <span>© {year} {COMPANY.legalName}. All rights reserved.</span>
          <span className="text-silver-400/70">Ajman · United Arab Emirates</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
